/**
 * Projections Export Utilities
 *
 * Functions for exporting Monthly Seasonality projection data to Excel.
 * Mirrors the structure of the PDF print output with full account-level detail.
 * Uses xlsx-js-style for cell formatting (bold, colors, fills).
 */

import * as XLSX from 'xlsx-js-style';
import { MonthlyTotals, MONTH_NAMES, getMonthValue, createEmptyMonthlyTotals } from '../types';

/**
 * Data structure for an individual expense account
 */
export interface ExportAccount {
  code: string;
  description: string;
  monthlyValues: MonthlyTotals;
  annualTotal: number;
  percentOfGross?: number;  // Optional percentage display
}

/**
 * Data structure for an expense category (e.g., Variable, Semi-Fixed, Fixed)
 */
export interface ExportCategory {
  name: string;
  displayName: string;
  monthlyValues: MonthlyTotals;
  annualTotal: number;
  accounts: ExportAccount[];  // Individual accounts within this category
}

/**
 * Data structure for a single row in the export
 */
export interface ExportRow {
  label: string;
  monthlyValues: MonthlyTotals;
  annualTotal: number;
  isHeader?: boolean;      // Section header (e.g., "New Vehicle Department")
  isSubHeader?: boolean;   // Subsection header (e.g., "Variable Expenses")
  isTotalRow?: boolean;    // Total row (bold)
  indent?: number;         // Indentation level (0-2)
  isUnits?: boolean;       // True if this row represents units (not currency)
}

/**
 * Department data for export
 */
export interface DepartmentExportData {
  name: string;
  displayName: string;
  rows: ExportRow[];
  expenseCategories?: ExportCategory[];  // Optional detailed expense breakdown
}

/**
 * Complete export data structure
 */
export interface ProjectionsExportData {
  locationName: string;
  targetYear: number;
  locationSummary: {
    totalGross: number;
    totalExpenses: number;
    operatingProfit: number;
    monthlyGross: MonthlyTotals;
    monthlyExpenses: MonthlyTotals;
    monthlyProfit: MonthlyTotals;
  };
  departments: DepartmentExportData[];
  generatedAt: Date;
}

/**
 * Get month values as an array
 */
function getMonthlyArray(totals: MonthlyTotals): number[] {
  return MONTH_NAMES.map((_, i) => getMonthValue(totals, i + 1));
}

// ========================================
// STYLE DEFINITIONS (matching on-screen colors)
// ========================================

const STYLES = {
  // Title row
  title: {
    font: { bold: true, sz: 14, color: { rgb: '1F2937' } },
  },
  // Subtitle (generated date)
  subtitle: {
    font: { sz: 10, color: { rgb: '6B7280' } },
  },
  // Column header row (Metric, Jan, Feb, ..., Total)
  columnHeader: {
    font: { bold: true, sz: 10, color: { rgb: 'FFFFFF' } },
    fill: { fgColor: { rgb: '374151' } },
    alignment: { horizontal: 'center' as const },
    border: {
      bottom: { style: 'thin' as const, color: { rgb: '1F2937' } },
    },
  },
  columnHeaderLeft: {
    font: { bold: true, sz: 10, color: { rgb: 'FFFFFF' } },
    fill: { fgColor: { rgb: '374151' } },
    alignment: { horizontal: 'left' as const },
    border: {
      bottom: { style: 'thin' as const, color: { rgb: '1F2937' } },
    },
  },
  // Section headers (LOCATION SUMMARY, NEW VEHICLE DEPARTMENT, etc.)
  sectionHeader: {
    font: { bold: true, sz: 11, color: { rgb: '1F2937' } },
    fill: { fgColor: { rgb: 'DBEAFE' } },
    border: {
      bottom: { style: 'thin' as const, color: { rgb: '93C5FD' } },
    },
  },
  // Gross rows (green tint)
  gross: {
    font: { bold: true, sz: 10, color: { rgb: '065F46' } },
    fill: { fgColor: { rgb: 'D1FAE5' } },
  },
  // Indented gross sub-rows (Front-End Gross, F&I Income)
  grossIndented: {
    font: { sz: 10, color: { rgb: '065F46' } },
    fill: { fgColor: { rgb: 'ECFDF5' } },
  },
  // Units rows (blue tint)
  units: {
    font: { bold: true, sz: 10, color: { rgb: '1E40AF' } },
    fill: { fgColor: { rgb: 'DBEAFE' } },
  },
  // Expense category headers (orange tint) - Variable, Semi-Fixed, etc.
  expenseCategory: {
    font: { bold: true, sz: 10, color: { rgb: '92400E' } },
    fill: { fgColor: { rgb: 'FEF3C7' } },
    border: {
      bottom: { style: 'thin' as const, color: { rgb: 'FCD34D' } },
    },
  },
  // Individual account rows (light gray)
  account: {
    font: { sz: 9, color: { rgb: '4B5563' } },
    fill: { fgColor: { rgb: 'F9FAFB' } },
  },
  // Total Expenses row (red tint)
  totalExpenses: {
    font: { bold: true, sz: 10, color: { rgb: '991B1B' } },
    fill: { fgColor: { rgb: 'FEE2E2' } },
    border: {
      top: { style: 'thin' as const, color: { rgb: 'FCA5A5' } },
    },
  },
  // Operating Profit row (emerald tint)
  operatingProfit: {
    font: { bold: true, sz: 10, color: { rgb: '065F46' } },
    fill: { fgColor: { rgb: 'A7F3D0' } },
    border: {
      top: { style: 'thin' as const, color: { rgb: '6EE7B7' } },
      bottom: { style: 'medium' as const, color: { rgb: '059669' } },
    },
  },
  // Other Income category header
  otherIncomeCategory: {
    font: { bold: true, sz: 10, color: { rgb: '065F46' } },
    fill: { fgColor: { rgb: 'D1FAE5' } },
    border: {
      bottom: { style: 'thin' as const, color: { rgb: '6EE7B7' } },
    },
  },
};

/**
 * Apply a style to all cells in a row
 */
function applyRowStyle(
  worksheet: XLSX.WorkSheet,
  rowIndex: number,
  colCount: number,
  style: Record<string, unknown>,
  numberFormat?: string,
): void {
  for (let C = 0; C <= colCount; C++) {
    const cellAddress = XLSX.utils.encode_cell({ r: rowIndex, c: C });
    if (!worksheet[cellAddress]) {
      worksheet[cellAddress] = { t: 's', v: '' };
    }
    worksheet[cellAddress].s = {
      ...style,
      // Right-align numbers (columns B-N), left-align labels (column A)
      alignment: C === 0
        ? (style as Record<string, unknown>).alignment || { horizontal: 'left' as const }
        : { horizontal: 'right' as const },
    };
    // Apply number format to data columns
    if (C > 0 && numberFormat && worksheet[cellAddress].t === 'n') {
      worksheet[cellAddress].z = numberFormat;
    }
  }
}

/**
 * Export projections data to Excel file
 * Includes full account-level detail matching the PDF print output
 */
export function exportProjectionsToExcel(data: ProjectionsExportData): void {
  const workbook = XLSX.utils.book_new();

  // Create the main worksheet data as array of arrays
  const wsData: (string | number | null)[][] = [];

  // Track row types for styling
  const rowTypes: Map<number, string> = new Map();

  // Header section
  wsData.push([`${data.locationName} - Monthly Projections ${data.targetYear}`]);
  rowTypes.set(0, 'title');
  wsData.push([`Generated: ${data.generatedAt.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })}`]);
  rowTypes.set(1, 'subtitle');
  wsData.push([]); // Empty row

  // Column headers
  const headerRow: (string | number | null)[] = ['Metric', ...MONTH_NAMES, `${data.targetYear} Total`];
  wsData.push(headerRow);
  rowTypes.set(3, 'columnHeader');

  // Location Summary section
  rowTypes.set(wsData.length, 'sectionHeader');
  wsData.push(['LOCATION SUMMARY', ...Array(13).fill(null)]);

  rowTypes.set(wsData.length, 'gross');
  wsData.push([
    'Total Gross',
    ...getMonthlyArray(data.locationSummary.monthlyGross),
    data.locationSummary.totalGross,
  ]);
  rowTypes.set(wsData.length, 'totalExpenses');
  wsData.push([
    'Total Expenses',
    ...getMonthlyArray(data.locationSummary.monthlyExpenses),
    data.locationSummary.totalExpenses,
  ]);
  rowTypes.set(wsData.length, 'operatingProfit');
  wsData.push([
    'Operating Profit',
    ...getMonthlyArray(data.locationSummary.monthlyProfit),
    data.locationSummary.operatingProfit,
  ]);
  wsData.push([]); // Empty row separator

  // Department sections
  data.departments.forEach(dept => {
    // Department header
    rowTypes.set(wsData.length, 'sectionHeader');
    wsData.push([dept.displayName.toUpperCase(), ...Array(13).fill(null)]);

    // Department summary rows (Gross, Units if applicable)
    dept.rows.forEach(row => {
      // Only output gross and units rows here, not expenses/profit (those come after categories)
      if (row.label === 'Total Gross' || row.label === 'Other Income' || row.label === 'Units' ||
          row.label === 'Front-End Gross' || row.label === 'F&I Income' || row.label === 'Gross Per Unit' ||
          row.label === 'Rental Income') {
        const prefix = row.indent ? '  '.repeat(row.indent) : '';

        if (row.isUnits) {
          rowTypes.set(wsData.length, 'units');
        } else if (row.indent) {
          rowTypes.set(wsData.length, 'grossIndented');
        } else {
          rowTypes.set(wsData.length, 'gross');
        }

        wsData.push([
          `${prefix}${row.label}`,
          ...getMonthlyArray(row.monthlyValues),
          row.annualTotal,
        ]);
      }
    });

    // Expense Categories with account-level detail
    if (dept.expenseCategories && dept.expenseCategories.length > 0) {
      dept.expenseCategories.forEach(category => {
        // Skip empty categories
        if (category.annualTotal === 0 && category.accounts.length === 0) return;

        // Determine if this is an income category (OTHER_INCOME)
        const isIncomeCategory = category.name === 'OTHER_INCOME';

        // Category header row (e.g., "Variable Expenses")
        rowTypes.set(wsData.length, isIncomeCategory ? 'otherIncomeCategory' : 'expenseCategory');
        const accountCount = category.accounts.length;
        const categoryLabel = accountCount > 0
          ? `  ${category.displayName} (${accountCount})`
          : `  ${category.displayName}`;
        wsData.push([
          categoryLabel,
          ...getMonthlyArray(category.monthlyValues),
          category.annualTotal,
        ]);

        // Individual account rows
        category.accounts.forEach(account => {
          rowTypes.set(wsData.length, 'account');
          const accountLabel = `    ${account.code} - ${account.description}`;
          wsData.push([
            accountLabel,
            ...getMonthlyArray(account.monthlyValues),
            account.annualTotal,
          ]);
        });
      });
    }

    // Total Expenses and Operating Profit rows (at the end of department)
    dept.rows.forEach(row => {
      if (row.label === 'Total Expenses') {
        rowTypes.set(wsData.length, 'totalExpenses');
        wsData.push([
          row.label,
          ...getMonthlyArray(row.monthlyValues),
          row.annualTotal,
        ]);
      } else if (row.label === 'Operating Profit') {
        rowTypes.set(wsData.length, 'operatingProfit');
        wsData.push([
          row.label,
          ...getMonthlyArray(row.monthlyValues),
          row.annualTotal,
        ]);
      }
    });

    wsData.push([]); // Empty row between departments
  });

  // Create worksheet from array
  const worksheet = XLSX.utils.aoa_to_sheet(wsData);

  // Set column widths - wider first column to accommodate account descriptions
  worksheet['!cols'] = [
    { wch: 45 },  // Metric column (wider for account codes + descriptions)
    ...MONTH_NAMES.map(() => ({ wch: 13 })),  // Month columns (slightly wider for styled numbers)
    { wch: 15 },  // Annual total column
  ];

  // Apply number formatting and styles to all data cells
  const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1');
  const colCount = 13; // 0=Metric, 1-12=months, 13=total

  // First apply number formatting
  for (let R = 4; R <= range.e.r; R++) {
    const rowType = rowTypes.get(R);
    for (let C = 1; C <= 13; C++) {
      const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
      const cell = worksheet[cellAddress];
      if (cell && typeof cell.v === 'number') {
        if (rowType === 'units') {
          cell.z = '#,##0';
        } else {
          cell.z = '"$"#,##0_);("$"#,##0)';
        }
      }
    }
  }

  // Apply row styles
  rowTypes.forEach((type, rowIndex) => {
    const style = (STYLES as Record<string, Record<string, unknown>>)[type];
    if (!style) return;

    const numFmt = type === 'units' ? '#,##0' : '"$"#,##0_);("$"#,##0)';
    applyRowStyle(worksheet, rowIndex, colCount, style, numFmt);
  });

  // Special handling: column header row needs center alignment for month names
  const colHeaderIdx = 3;
  for (let C = 0; C <= colCount; C++) {
    const cellAddress = XLSX.utils.encode_cell({ r: colHeaderIdx, c: C });
    if (worksheet[cellAddress]) {
      worksheet[cellAddress].s = C === 0 ? STYLES.columnHeaderLeft : STYLES.columnHeader;
    }
  }

  // Add worksheet to workbook
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Monthly Projections');

  // Generate filename
  const dateStr = new Date().toISOString().split('T')[0];
  const locationSlug = data.locationName.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase();
  const filename = `projections-${locationSlug}-${data.targetYear}-${dateStr}.xlsx`;

  // Download the file
  XLSX.writeFile(workbook, filename);
}

/**
 * Build export data from SeasonalityModeView state
 * This function should be called from the component with all necessary data
 */
export function buildProjectionsExportData(
  locationName: string,
  targetYear: number,
  locationTotals: {
    totalGross: number;
    totalExpenses: number;
    operatingProfit: number;
    monthlyGross: MonthlyTotals;
    monthlyExpenses: MonthlyTotals;
    monthlyProfit: MonthlyTotals;
  },
  departmentData: DepartmentExportData[]
): ProjectionsExportData {
  return {
    locationName,
    targetYear,
    locationSummary: locationTotals,
    departments: departmentData,
    generatedAt: new Date(),
  };
}

/**
 * Helper to create an export row
 */
export function createExportRow(
  label: string,
  monthlyValues: MonthlyTotals,
  annualTotal: number,
  options?: {
    isHeader?: boolean;
    isSubHeader?: boolean;
    isTotalRow?: boolean;
    indent?: number;
    isUnits?: boolean;
  }
): ExportRow {
  return {
    label,
    monthlyValues,
    annualTotal,
    ...options,
  };
}

/**
 * Helper to create empty monthly totals for rows without monthly data
 */
export function emptyMonthlyTotals(): MonthlyTotals {
  return createEmptyMonthlyTotals();
}
